import { Loader2, Phone, PhoneIncoming, PhoneOutgoing } from 'lucide-react';

import { Badge } from '@apps/web/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import { CallDetailView } from '@apps/web/components/voice_calls/call_detail_view';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { priorityBadgeClasses } from '@apps/web/lib/activity_event_priority';
import { isS3RecordingUrl } from '@apps/web/lib/contact_utils';
import { cn } from '@apps/web/lib/utils';
import {
    useOrganizationVoiceCall,
    useUser,
    useVoiceCallNotes,
    useVoiceCallRecordingUrls,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type ApiResponseType,
    type VoiceCallNote,
} from '@libraries/typescript/api_client';

type OrganizationVoiceCall = ApiResponseType<
    '/organizations/{organization_id}/voice-calls/{voice_call_id}',
    'get'
>;

function VoiceCallNotesSection({
    organizationId,
    voiceCallId,
}: {
    readonly organizationId: string;
    readonly voiceCallId: string;
}) {
    const { data, error, isLoading } = useVoiceCallNotes(
        organizationId,
        voiceCallId,
    );
    const notes: ReadonlyArray<VoiceCallNote> = data?.notes ?? [];

    if (isLoading) {
        return null;
    }
    if (error || notes.length === 0) {
        return null;
    }
    return (
        <div className="space-y-2 px-6 pt-2">
            <h3 className="text-sm font-medium">Notes</h3>
            <div className="space-y-2">
                {notes.map((note, index) => (
                    <div
                        key={note.id ?? `note-${index}`}
                        className="space-y-1 rounded-lg border p-3"
                    >
                        <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-semibold">
                                {note.title}
                            </p>
                            <Badge
                                variant="outline"
                                className="px-1.5 py-0 text-[10px] tracking-wide uppercase"
                            >
                                {note.category}
                            </Badge>
                            <Badge
                                variant="outline"
                                className={cn(
                                    'px-1.5 py-0',
                                    priorityBadgeClasses(note.priority),
                                )}
                            >
                                {note.priority}
                            </Badge>
                            {note.action_required ? (
                                <Badge
                                    variant="warning"
                                    className="px-1.5 py-0"
                                >
                                    Action needed
                                </Badge>
                            ) : null}
                        </div>
                        <p className="text-sm">{note.body}</p>
                        {note.evidence_quote ? (
                            <p className="text-muted-foreground border-l-2 pl-2 text-xs italic">
                                {note.evidence_quote}
                            </p>
                        ) : null}
                    </div>
                ))}
            </div>
        </div>
    );
}

export function CallDetailDialog({
    organizationId,
    voiceCallId,
    prefetchedCall,
    caregiverName,
    open,
    onOpenChange,
    timezone,
}: {
    readonly organizationId: string;
    readonly voiceCallId: string | null;
    readonly prefetchedCall?: OrganizationVoiceCall | null;
    readonly caregiverName?: string | null;
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly timezone: string;
}) {
    const { data: user } = useUser({
        reason: 'call-detail-dialog',
    });
    const shouldFetchCall = open && !!voiceCallId;
    const {
        data: fetchedCall,
        error,
        isLoading,
        mutate,
    } = useOrganizationVoiceCall(
        organizationId,
        shouldFetchCall ? voiceCallId : null,
        {
            reason: 'call-detail-dialog',
        },
    );
    const call = fetchedCall ?? prefetchedCall ?? null;
    const isLoadingCall = shouldFetchCall ? isLoading && !call : false;
    const callError = shouldFetchCall && !call ? error : null;

    useListenForOrganizationEvent('voice_call_updated', (event) => {
        if (!open || !voiceCallId) {
            return;
        }
        if (event.resource.type !== 'voice_call') {
            return;
        }
        if (event.resource.id !== voiceCallId) {
            return;
        }

        void mutate();
    });

    const conversationalCall = call?.kind === 'notification' ? null : call;
    const recordingVoiceCallIds =
        conversationalCall?.recording_url &&
        isS3RecordingUrl(conversationalCall.recording_url)
            ? [conversationalCall.id]
            : null;
    const {
        data: recordingUrls,
        error: recordingUrlsError,
        isLoading: isRecordingUrlLoading,
    } = useVoiceCallRecordingUrls(organizationId, recordingVoiceCallIds);
    const titleText =
        call?.direction === 'inbound'
            ? 'Inbound call'
            : call?.direction === 'outbound'
              ? 'Outbound call'
              : 'Call';
    const TitleIcon =
        call?.direction === 'inbound'
            ? PhoneIncoming
            : call?.direction === 'outbound'
              ? PhoneOutgoing
              : Phone;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[85vh] w-[95vw] p-0 sm:max-w-6xl">
                <DialogHeader className="border-b px-6 py-4">
                    <DialogTitle className="flex items-center gap-2">
                        <TitleIcon className="h-4 w-4 text-blue-600" />
                        {titleText}
                    </DialogTitle>
                    <DialogDescription>
                        Review the call details
                    </DialogDescription>
                </DialogHeader>
                <ScrollArea className="max-h-[calc(85vh-96px)]">
                    {isLoadingCall ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
                        </div>
                    ) : callError || !call ? (
                        <div className="px-6 pt-2 pb-4">
                            <p className="text-sm text-red-600">
                                Unable to load call details.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3 pb-4">
                            {call.kind !== 'notification' && (
                                <VoiceCallNotesSection
                                    organizationId={organizationId}
                                    voiceCallId={call.id}
                                />
                            )}
                            <div className="space-y-3 px-6 pt-2">
                                <CallDetailView
                                    call={call}
                                    caregiverName={
                                        caregiverName ??
                                        call.caller_name ??
                                        null
                                    }
                                    isAdmin={Boolean(user?.admin)}
                                    isRecordingUrlLoading={
                                        isRecordingUrlLoading
                                    }
                                    organizationId={organizationId}
                                    prefetchedRecordingUrl={
                                        recordingUrls?.[call.id]
                                    }
                                    recordingUrlError={recordingUrlsError}
                                    silentListeningLabelSettingsEnabled={false}
                                    timezone={timezone}
                                    flatTranscript={true}
                                />
                            </div>
                        </div>
                    )}
                </ScrollArea>
            </DialogContent>
        </Dialog>
    );
}
